// Dispatcher
import { PropsWithChildren, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { ORDERS_API } from 'configs/api';
import { DEFAULT_HOURS_FILTERS, DEFAULT_TABS_PROPS } from 'configs/common';
import { useAuthenticatedInformation, useGlobalRefreshTimer, useLocaleTimeUtils } from 'hooks';
import { IDispatcherOrder, IOrderMetrics } from 'types/api';
import { IListResponse, ITabItem, Nullable } from 'types/common';
import { THourRanges } from 'components/HoursFilterGroup';
import { DATE_SORT_CONFIG, TSortOrder } from 'pages/Dispatcher/DateSortRadioGroup';
import DispatcherTabPane from 'pages/Dispatcher/DispatcherTabPane';
import { IOrderStatusUpdate, IOrderStatusUpdateModalHandle } from 'pages/Dispatcher/OrderStatusUpdateModal';
import { ACTION_TO_STATUS } from 'pages/Dispatcher/OrdersCards';
import {
	DISPATCHER_DEFAULT_ACTIVE_TAB,
	DISPATCHER_FILTER_TABS,
	DISPATCHER_TAB_CONFIG_SET,
	ORDERS_PER_PAGE,
	getDispatcherTabKey,
} from 'pages/Dispatcher/configs';
import { transformDataToCardData } from 'pages/Dispatcher/helper';
import {
	EDispatcherOrderCardActionsBtnKeys,
	EDispatcherTab,
	EDispatcherTabsTranslationKeys,
	IBranchDispatcherOrderListFilterRequest,
	IOrderCardData,
} from 'pages/Dispatcher/types';
import { THttpRequestParams, useHttpClient } from './useHttpClient';
import { message } from 'antd';

// ! CONTEXT LOGIC HOOK
const useDispatcherLogic = () => {
	const { t: tDispatcher } = useTranslation('dispatcher');

	const refreshControlUnit = useGlobalRefreshTimer();

	const dispatcherHttpClient = useHttpClient<IListResponse<IDispatcherOrder>>();

	const { selectedBranchIdList: branchIdList } = useAuthenticatedInformation();
	const { getStartDateParamToFilter } = useLocaleTimeUtils();

	// ! refs
	const orderStatusUpdateModalRef = useRef<IOrderStatusUpdateModalHandle>(null);

	// ! states
	const [count, setCount] = useState<number>(0);
	const [currentPage, setCurrentPage] = useState<number>(1);

	const [orderCodeSearchValue, setOrderCodeSearchValue] = useState('');
	const [branchNameSearchValue, setBranchNameSearchValue] = useState('');

	const [cardsData, setCardsData] = useState<IOrderCardData[]>([]);

	const fetchGlobalMetricsHttpClient = useHttpClient<IOrderMetrics>();
	const updateOrderStatusHttpClient = useHttpClient();

	const [deliveryStatusFilters, setDeliveryStatusFilters] = useState<string[]>([]);
	const [isDeliveryStatusDropdownOpen, setIsDeliveryStatusDropdownOpen] = useState<boolean>(false);

	const [globalMetrics, setGlobalMetrics] = useState<Nullable<IOrderMetrics>>(null);
	const [activeStatusTab, setActiveStatusTab] = useState<EDispatcherTab>(DISPATCHER_DEFAULT_ACTIVE_TAB);

	const [dateSort, setDateSort] = useState<TSortOrder>(DATE_SORT_CONFIG[1].value);
	const [hoursFilter, setHoursFilter] = useState<THourRanges>(DEFAULT_HOURS_FILTERS[0]);

	// ! handlers
	const handleTabChange = (newTab: string) => {
		if (!dispatcherHttpClient.isLoading) {
			// reset values
			setCurrentPage(1);

			setDeliveryStatusFilters([]);
			setIsDeliveryStatusDropdownOpen(false);
			// set new active tab

			const dispatcherEnumKey = getDispatcherTabKey(newTab);
			setActiveStatusTab(dispatcherEnumKey);
		}
	};

	const handleDataRefreshChange = (value: boolean) => {
		refreshControlUnit.toggleRefreshEnable(value);
	};

	const handleChangeDateSort = (sort: TSortOrder) => {
		setDateSort(sort);
	};

	const handleHoursFilterChange = (hours: THourRanges) => {
		setHoursFilter(hours);
	};

	const fetchGlobalMetrics = async (start_date?: string) => {
		if (!branchIdList) return;

		fetchGlobalMetricsHttpClient.request({
			requestConfig: ORDERS_API.dispatcherBadgesMetrics(branchIdList, start_date),
			successCallback: (globalMetrics) => {
				setGlobalMetrics(globalMetrics);
			},
			displayErrorMsg: false,
		});
	};

	// ! effects
	useEffect(
		() => {
			const ctrl = new AbortController();

			const defaultParams: Partial<IBranchDispatcherOrderListFilterRequest> = {
				page: currentPage.toString(),
				per_page: ORDERS_PER_PAGE,
				start_date: getStartDateParamToFilter(+hoursFilter),
				sort_by: 'placed_at',
				order_by: dateSort,
			};

			const filters = DISPATCHER_TAB_CONFIG_SET[activeStatusTab]?.filter || {};

			let searchParams = {};
			if (orderCodeSearchValue?.trim().length) {
				searchParams = { ...searchParams, code: orderCodeSearchValue };
			}
			if (branchNameSearchValue?.trim().length) {
				searchParams = { ...searchParams, store_name: branchNameSearchValue };
			}

			const finalParams: THttpRequestParams = {
				...(defaultParams as THttpRequestParams),
				...(filters as THttpRequestParams),
				...(searchParams as THttpRequestParams),
			};

			// Fetch data
			if (!isDeliveryStatusDropdownOpen) {
				fetchGlobalMetrics(defaultParams.start_date);

				dispatcherHttpClient.request(
					{
						requestConfig: ORDERS_API.dispatcherList(branchIdList, finalParams),
						successCallback: (response) => {
							const cards = transformDataToCardData(response.data);
							setCardsData(cards);
							setCount(response.count);
						},
					},
					ctrl.signal
				);
			}

			// Unsubscribing (cancel the request)
			return () => ctrl.abort();
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			refreshControlUnit.refreshId,
			activeStatusTab,
			currentPage,
			orderCodeSearchValue,
			branchNameSearchValue,
			hoursFilter,
			dateSort,
			deliveryStatusFilters,
			isDeliveryStatusDropdownOpen,
		]
	);

	const handleCardActionClick = (
		branchId: number,
		orderId: number,
		actionKey: EDispatcherOrderCardActionsBtnKeys
	) => {
		// open order status update form modal
		orderStatusUpdateModalRef.current?.open({ id: orderId, statusActionKey: actionKey });
	};

	// ! order status update form modal
	const onOrderStatusUpdateConfirm = (order: IOrderStatusUpdate, comment: string) => {
		return updateOrderStatusHttpClient.request({
			requestConfig: ORDERS_API.patchStatus(order.id, {
				status: ACTION_TO_STATUS[order.statusActionKey],
				comment,
			}),

			successCallback: () => {
				message.success(tDispatcher('order_status_update_modal.feedback.success'), 5);
				refreshControlUnit.onRefresh();
			},
		});
	};

	const tabItems: ITabItem[] = useMemo(
		() =>
			DISPATCHER_FILTER_TABS.map((tabKey) => DISPATCHER_TAB_CONFIG_SET[tabKey]).map<ITabItem>(
				({ key, extractMetricFn }) => ({
					key: key.toString(),
					label: (
						<DispatcherTabPane
							labelKey={EDispatcherTabsTranslationKeys[key]}
							loading={dispatcherHttpClient.isLoading}
							badgeCount={globalMetrics ? extractMetricFn(globalMetrics) : undefined}
						/>
					),
					style: DEFAULT_TABS_PROPS,
				})
			),
		[globalMetrics, dispatcherHttpClient.isLoading]
	);

	//
	return {
		tabItems,
		orderCodeSearchValue,
		handleCardActionClick,
		setOrderCodeSearchValue,
		branchNameSearchValue,
		setBranchNameSearchValue,
		count,
		currentPage,
		cardsData,
		setCurrentPage,
		orderStatusUpdateModalRef,
		onOrderStatusUpdateConfirm,
		updateOrderStatusHttpClient,

		//
		dateSort,
		hoursFilter,
		activeStatusTab,
		isDataRefreshEnable: refreshControlUnit.isEnable,

		refreshControlUnit,
		dispatcherHttpClient,

		handleTabChange,
		handleChangeDateSort,
		handleHoursFilterChange,
		handleDataRefreshChange,
	};
};

// ! CONTEXT
type IDispatcherContextData = ReturnType<typeof useDispatcherLogic>;
const DispatcherContext = createContext<IDispatcherContextData | undefined>(undefined);

// ! CONTEXT PROVIDER
export const DispatcherProvider = ({ children = <Outlet /> }: PropsWithChildren) => {
	const value = useDispatcherLogic();

	return <DispatcherContext.Provider value={value}>{children}</DispatcherContext.Provider>;
};

// ! CONTEXT CONSUMER HOOK
export const useDispatcher = () => {
	const ctx = useContext(DispatcherContext);

	if (!ctx) {
		throw new Error('useDispatcher must be used inside DispatcherProvider');
	}

	return ctx;
};
